import {
  BulletinResource,
  extractImagesFromPdf,
  extractTextFromPdf,
} from "@rubi/api-generated";

/**
 * The "from a PDF" entry point's one API call — or rather two, because the backend splits the work.
 *
 * `POST /bulletin/text_from_pdf` does more than its name says: it **creates the bulletin row**,
 * stores the PDF on it, runs the AI extraction and writes the result into the columns. So by the
 * time it answers there is a real draft with an id, which is why this entry point needs no source
 * of its own in `useBulletinSource` — the form opens on the edit route and loads it like any other
 * existing bulletin.
 *
 * `POST /bulletin/{id}/images_from_pdf` is the second half: it pulls the photos out of the same file
 * and stores them as attachments. It needs the id, so it cannot run in parallel.
 *
 * ## Why a failed image pass is not a failed import
 *
 * The two calls fail differently. If the text pass fails, nothing was created and the officer can
 * try again. If the **image** pass fails, a draft already exists with everything the AI read out of
 * the PDF — throwing that away to report an error would lose the expensive half of the work. So the
 * caller is told what happened and opens the form either way; the officer can drop the photos in by
 * hand, which is what the media section is for.
 */
export interface PdfImportResult {
  bulletinId: number;
  /** The draft exists and holds the text, but its photos were not extracted. */
  imagesFailed: boolean;
}

export const importBulletinFromPdf = async (
  file: File,
): Promise<PdfImportResult> => {
  const extracted = await extractTextFromPdf({ body: { file } });
  if (extracted.error || !extracted.data) {
    throw extracted.error ?? new Error("The PDF could not be read.");
  }

  // The endpoint answers with a whole bulletin; the id is all this needs, because the form reads the
  // document back through the edit route rather than being handed values.
  const { id } = extracted.data as BulletinResource;

  try {
    const images = await extractImagesFromPdf({
      path: { bulletin_id: id },
      body: { file },
    });
    if (images.error) throw images.error;
  } catch {
    return { bulletinId: id, imagesFailed: true };
  }

  return { bulletinId: id, imagesFailed: false };
};
